'use client';
import type { TpMessage, TpMessageReaction } from '@features/AiAssistent/types';
import { type Observable, observable } from '@legendapp/state';
import { ObservablePersistLocalStorage } from '@legendapp/state/persist-plugins/local-storage';
import { syncObservable } from '@legendapp/state/sync';
import { createContext, type ReactNode, useContext, useEffect } from 'react';

type RootState = {
  user: {
    email: string;
    userId: string;
    isSubscriptionActive: boolean;
    wasInactiveSubscriptionClosed: boolean;
  };
  recoveryData: {
    email: string;
    targetTime: number;
  }[];
  chat: {
    isChatStarted: boolean;
    isTyping: boolean;
    threadId: string | null;
    threads: Array<{
      id: string;
      name: string;
    }>;
    messages: TpMessage[];
    setMessageReaction: ({
      id,
      reaction,
    }: {
      id: string;
      reaction: TpMessageReaction;
    }) => void;
  };
};

const store$ = observable<RootState>({
  user: {
    email: '',
    userId: '',
    isSubscriptionActive: false,
    wasInactiveSubscriptionClosed: false,
  },
  recoveryData: [],
  chat: {
    isChatStarted: false,
    isTyping: false,
    threadId: null,
    threads: [],
    messages: [],
    setMessageReaction: ({ id, reaction }) => {
      const newMessages = [...store$.chat.messages.get()];
      const messageToEdit = newMessages.find((m) => m.id === id);
      if (messageToEdit) {
        messageToEdit.reaction === reaction
          ? (messageToEdit.reaction = null)
          : (messageToEdit.reaction = reaction);
      }
      store$.chat.messages.set(newMessages);
    },
  },
});

syncObservable(store$.user, {
  persist: {
    name: 'persistenceUserStore',
    plugin: ObservablePersistLocalStorage,
  },
});

syncObservable(store$.recoveryData, {
  persist: {
    name: 'persistenceRecoveryDataStore',
    plugin: ObservablePersistLocalStorage,
  },
});

const RootStoreContext = createContext<Observable<RootState>>(store$);

export const RootStoreProvider = ({
  children,
  emailFromCookie,
  userIdFromCookie,
}: {
  children: ReactNode;
  emailFromCookie?: string;
  userIdFromCookie?: string;
}) => {
  useEffect(() => {
    if (emailFromCookie) {
      store$?.user.email.set(emailFromCookie);
    }
  }, [emailFromCookie]);

  useEffect(() => {
    if (userIdFromCookie) {
      store$?.user.userId.set(userIdFromCookie);
    }
  }, [userIdFromCookie]);

  return (
    <RootStoreContext.Provider value={store$}>
      {children}
    </RootStoreContext.Provider>
  );
};

export const useRootStore = (): Observable<RootState> => {
  const store$ = useContext(RootStoreContext);
  if (!store$) {
    throw new Error('useRootStore must be used within a RootStoreProvider');
  }
  return store$;
};
